import { ApolloClient, HttpLink, InMemoryCache, from, makeVar } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import { customFetchBuilder, errors, TGlobalError } from '/packages/shared-components/utils/apollo';

export const cache = new InMemoryCache({
  typePolicies: {
    TestWithStatus: {
      keyFields: ['id', 'projectId'],
    },
    BaseCertificationSuite: {
      keyFields: ['id', 'cacheId'],
    },
    Project: {
      keyFields: ['id'],
    },
    Partner: {
      merge: true,
    },
    Model: {
      keyFields: ['id'],
    },
    Submission: {
      keyFields: ['id'],
    },
    Device: {
      keyFields: ['id'],
    },
    UserProfile: {
      keyFields: ['id'],
    },
    Content: {
      keyFields: ['elementId'],
    },
    TestExecution: {
      keyFields: ['id'],
    },
    TestComment: {
      keyFields: ['id'],
    },
    TestVersionUpdate: {
      keyFields: ['id'],
    },
    TestSet: {
      keyFields: ['id'],
    },
    Provider: {
      keyFields: ['id'],
    },
    Issuer: {
      keyFields: ['id'],
    },
    ConfigurationStatus: {
      keyFields: ['id'],
    },
    Offer: {
      keyFields: ['id'],
    },
    APITest: {
      keyFields: ['id'],
    },
    AdminUserProfile: {
      keyFields: ['email'],
    },
    APITestLabel: {
      keyFields: ['label'],
    },
    APITestPlan: {
      keyFields: ['id'],
      merge: true,
    },
    APITestPlanExecutionStatus: {
      merge: true,
    },
    Version: {
      merge: true,
    },
    ProjectProgress: {
      merge: true,
    },
    ADKTest: {
      keyFields: ['id', 'cacheId'],
    },
  },
  possibleTypes: {
    BaseInputInterface: ['TextInputType', 'NumberInputType', 'SelectInputType', 'RadioInputType', 'CheckboxInputType'],
  },
});

const globalError = makeVar<TGlobalError>(null);

const throwUnauthorized = (response: Response) => {
  globalError('unauthorized');
  return Promise.reject(new Error(response.status.toString()));
};

const throwAccessDenied = (response: Response) => {
  globalError('denied');
  return Promise.reject(new Error(response.status.toString()));
};

const customFetch = customFetchBuilder({ onAccessDenied: throwAccessDenied, onUnauthorized: throwUnauthorized });

const errorLink = onError(({ graphQLErrors }) => {
  // Manage user permission errors
  if (graphQLErrors)
    graphQLErrors.forEach(({ message }) => {
      if (new RegExp(errors.noToken).test(message)) globalError('unauthorized');
      if (/User does not have (portal|ADK|API|Android TV) access/.test(message)) globalError('forbidden');
    });
});

const httpLink = new HttpLink({
  uri: process.env.PORTAL_GRAPHQL_URI,
  credentials: 'include',
  fetch: customFetch,
});

const authLink = setContext(async (_, { headers }) => {
  return {
    headers: {
      ...headers,
    },
  };
});

const client = new ApolloClient({ cache, link: from([authLink, errorLink, httpLink]) });

export { client, globalError };
